<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { computed } from 'vue';
import { type Types } from './types';

const props = withDefaults(
  defineProps<{
    hasError?: boolean;
    id: string;
    isDisabled?: boolean;
    isReadonly?: boolean;
    isRequired?: boolean;
    isValid?: boolean;
    maxLength?: number;
    minLength?: number;
    modelValue?: string | null;
    placeholder?: string;
    type?: Types;
  }>(),
  {
    type: 'text',
  },
);

const emit = defineEmits<{ 'update:modelValue': [value: string]; blur: [] }>();

const defaultMaxLengths: { [key: string]: number } = { email: 50 };
const defaultPlaceholders: { [key: string]: string } = {
  email: 'voorbeeld@email.nl',
  tel: '012 34 56 78',
  url: 'website.nl',
};
const maxLength = computed(() => props.maxLength || defaultMaxLengths[props.type]);
const placeholder = computed(() => props.placeholder || defaultPlaceholders[props.type]);
const value = useVModel(props, 'modelValue', emit);

function sanitizeNumericValue(input: string | null | undefined): string {
  if (!input) return '';
  const sanitized = input.replace(/[^0-9]/g, '');
  return maxLength.value ? sanitized.slice(0, maxLength.value) : sanitized;
}

const valueOrDefault = computed({
  get() {
    return props.isReadonly ? value.value || '-' : value.value;
  },
  set(newValue) {
    if (props.type === 'number') {
      value.value = sanitizeNumericValue(newValue);
    } else {
      value.value = newValue;
    }
  },
});

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;

  if (props.type === 'number') {
    const cursorPosition = target.selectionStart ?? 0;
    const sanitized = sanitizeNumericValue(target.value);

    if (target.value !== sanitized) {
      target.value = sanitized;
      // Restore cursor position
      target.setSelectionRange(cursorPosition, cursorPosition);
    }
    value.value = sanitized;
  } else {
    value.value = target.value;
  }
}
</script>

<template>
  <div class="v-input flex grow items-center">
    <input
      :value="valueOrDefault"
      class="styled-input"
      :class="[
        'v-input__field',
        { 'v-input__field--has-error': props.hasError },
        { 'v-input__field--is-valid': props.isValid },
      ]"
      data-testid="v-input__field"
      :disabled="props.isDisabled"
      :id="props.id"
      :inputmode="props.type === 'number' ? 'numeric' : undefined"
      :maxlength="maxLength"
      :minlength="props.minLength"
      :pattern="props.type === 'number' ? '[0-9]*' : undefined"
      :placeholder="placeholder"
      :readonly="props.isReadonly"
      :required="props.isRequired"
      :type="props.type === 'number' ? 'text' : props.type"
      @input="handleInput"
      @blur="emit('blur')"
    />
  </div>
</template>

<style scoped>
.v-input {
  min-height: var(--min-height-form-row);
}

.v-input__field {
  transition-property: border-color, background-color;
  transition-duration: 0.2s;

  &.v-input__field--has-error:not([type='radio']):not([type='checkbox']) {
    border-color: var(--danger-500) !important;
  }

  &.v-input__field--is-valid {
    border-color: rgb(var(--success-rgb)) !important;
    background-color: rgba(14, 161, 153, 0.1) !important;
  }
}
</style>
